import classnames from 'classnames'
import * as React from 'react'

import { isMobile } from '../../../app/shared/isMobile'
import { Icon } from '../icon'
import { PopoverTrigger, useHoverPopover } from '../popover'
import { SelectOption } from '../select'
import { useSelect } from '../select/hooks'
import { NativeSelect } from '../select/native-select'
import { SelectList } from '../select/select-list'
import { useToolbarContext } from './hooks'
import { IToolbarItem, ToolbarTheme } from './interfaces'
import { ToolbarButton } from './toolbar-button'

export interface ToolbarGroupProps {
  /**
   * Main button item
   */
  defaultItem: IToolbarItem

  /**
   * Current selected item id
   */
  isActive: boolean

  /**
   * Should indicate some action in progress
   */
  isInAction?: boolean

  /**
   * Array of items
   */
  groupItems: IToolbarItem[]

  /**
   * Expand group button label
   */
  groupTitle?: string

  /**
   * On item select callback
   */
  onChange: (id: string) => void

  /**
   * Is item disabled
   */
  disabled?: boolean

  /**
   * Tooltip message if item is disabled
   */
  disabledTooltip?: string
}

export function ToolbarGroup({
  defaultItem,
  isActive,
  groupItems,
  groupTitle,
  onChange,
  disabled,
  disabledTooltip,
}: ToolbarGroupProps) {
  const { theme, isStretched } = useToolbarContext()
  const [selectedItem, setSelectedItem] = React.useState(defaultItem)
  const groupId = groupTitle?.replace(/\s/g, '-').toLocaleLowerCase()
  const useMobileNativeSelect = isMobile()
  const isAlternativeTheme = theme === ToolbarTheme.alternative
  const popoverState = useHoverPopover({
    placement: isAlternativeTheme && isStretched ? 'bottom' : 'right-start',
  })
  const select = useSelect(
    popoverState,
    selectedItem.id,
    groupItems?.map((i) => ({ value: i.id, label: i.title })) ?? [],
    (option) => {
      setSelectedItem(groupItems![groupItems!.findIndex((item) => item.id === option.value)])
      onChange(option.value)
    }
  )

  return (
    <div
      className={classnames('relative flex', {
        grow: isAlternativeTheme && isStretched,
        'h-10 w-10': isAlternativeTheme && !isStretched && !FinvizSettings.hasRedesignEnabled,
      })}
    >
      <ToolbarButton
        as={PopoverTrigger}
        state={popoverState}
        contentClass="relative"
        data-testid={`toolbar-group-${groupId}-expand`}
        item={selectedItem}
        active={isActive}
        disabled={disabled}
        title={groupTitle}
        disabledTooltip={disabledTooltip}
        onClick={(ev: React.MouseEvent<HTMLButtonElement>) => {
          ev.preventDefault()
          ev.stopPropagation()
          onChange(selectedItem.id)
        }}
      >
        <Icon name={(isActive && selectedItem.iconActive) || selectedItem.icon} width={32} className="shrink-0" />
        <Icon name="toolExpand" width={32} className="absolute flex" />
      </ToolbarButton>

      {useMobileNativeSelect ? (
        <NativeSelect
          items={groupItems.map((item) => ({ value: item.id, label: item.title }))}
          onChange={select.onItemChange}
          onClick={() => onChange(selectedItem.id)}
        />
      ) : (
        <SelectList
          finalFocusRef={{ current: document.body }}
          state={popoverState}
          onChange={select.onItemChange}
          aria-label={disabled && disabledTooltip ? disabledTooltip : groupTitle}
          hasBackdrop={false}
          hasViewportHeightLimit={true}
        >
          {groupItems.map((item) => (
            <SelectOption
              key={item.id}
              leftContent={<Icon name={item.icon} className="mr-1.5 shrink-0" width={28} />}
              data-testid={`toolbar-group-${groupId}-${item.id}`}
            >
              {item.title}
            </SelectOption>
          ))}
        </SelectList>
      )}
    </div>
  )
}
